import { useRef, useState } from 'react';
import { api, ErrorApi } from '../api/cliente.js';

interface FilaTrabajadorImport {
  dni: string;
  nombre: string;
  sexo?: string;
  fechaNacimiento?: string; // YYYY-MM-DD
  // Derivados para vista previa
  _edadCalculada?: number | null;
}

interface ResultadoImport {
  insertados: number;
  actualizados: number;
  total: number;
}

/**
 * Calcula la edad a partir de una fecha de nacimiento (YYYY-MM-DD).
 */
function calcularEdad(fechaNac: string): number | null {
  try {
    const nac = new Date(fechaNac);
    if (isNaN(nac.getTime())) return null;
    const hoy = new Date();
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    return edad >= 0 && edad < 120 ? edad : null;
  } catch { return null; }
}

/**
 * Parsea un CSV con columnas: Documento, Nombre, Sexo, nacimiento (u otras variantes).
 * Separador: ; o ,
 */
function parsearCSV(texto: string): FilaTrabajadorImport[] {
  const lineas = texto
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lineas.length < 2) return [];

  // Detectar separador (preferencia por ;)
  const sep = lineas[0].includes(';') ? ';' : ',';
  const cabeceras = lineas[0]
    .split(sep)
    .map((h) => h.toLowerCase().trim().replace(/^"|"$/g, '').trim());

  // Mapear columnas de forma flexible
  const idx = (patterns: RegExp) =>
    cabeceras.findIndex((h) => patterns.test(h));

  const colDni = idx(/^(dni|documento|doc|nro|número|numero|cedula|cédula)$/);
  const colNombre = idx(/^(nombre|apellido|apellido_nombre|apellido y nombre|apellidos?.*nombre|nombre.*apellido)$/);
  const colSexo = idx(/^(sexo|genero|género|sex|gender)$/);
  const colNac = idx(/^(nacimiento|fecha_nacimiento|fecha\.nacimiento|birth|birthdate|nacido|nac\.)$/);

  if (colDni === -1 || colNombre === -1) {
    throw new Error(
      'El archivo debe tener al menos las columnas "Documento" y "Nombre".\n' +
      `Columnas detectadas: ${cabeceras.join(', ')}\n` +
      'Descargá la plantilla de ejemplo para ver el formato correcto.',
    );
  }

  const filas: FilaTrabajadorImport[] = [];
  for (let i = 1; i < lineas.length; i++) {
    const celdas = lineas[i].split(sep).map((c) => c.trim().replace(/^"|"$/g, '').trim());
    const dniRaw = celdas[colDni] ?? '';
    const nombre = celdas[colNombre] ?? '';

    // Limpiar el DNI: remover puntos, guiones, espacios
    const dni = dniRaw.replace(/[\s.\-]/g, '');

    if (!dni || !nombre) continue;

    const sexoRaw = colSexo >= 0 ? (celdas[colSexo] ?? '') : '';
    const nacRaw = colNac >= 0 ? (celdas[colNac] ?? '') : '';

    // Normalizar fecha de nacimiento a YYYY-MM-DD si viene en otro formato
    let fechaNacimiento: string | undefined;
    if (nacRaw) {
      // Intentar parsear como es (YYYY-MM-DD) o DD/MM/YYYY
      const isoMatch = nacRaw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const dmyMatch = nacRaw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (isoMatch) {
        fechaNacimiento = nacRaw;
      } else if (dmyMatch) {
        fechaNacimiento = `${dmyMatch[3]}-${dmyMatch[2].padStart(2, '0')}-${dmyMatch[1].padStart(2, '0')}`;
      }
    }

    const edadCalculada = fechaNacimiento ? calcularEdad(fechaNacimiento) : undefined;

    filas.push({
      dni,
      nombre,
      sexo: sexoRaw || undefined,
      fechaNacimiento,
      _edadCalculada: edadCalculada,
    });
  }
  return filas;
}

function descargarPlantilla() {
  const csv = [
    'Documento;Nombre;Sexo;nacimiento',
    '30123456;PEREZ JUAN MARIO;Hombre;1989-05-14',
    '27888999;RODRIGUEZ MARIA LAURA;Mujer;1981-11-22',
    '33444555;FUNES LUIS ALBERTO;Hombre;1996-03-08',
  ].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_empleados.csv';
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  onCerrar: () => void;
  onExito: () => void;
}

export function ImportarEmpleados({ onCerrar, onExito }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filas, setFilas] = useState<FilaTrabajadorImport[] | null>(null);
  const [errorParse, setErrorParse] = useState('');
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImport | null>(null);
  const [errorImport, setErrorImport] = useState('');
  const [nombreArchivo, setNombreArchivo] = useState('');

  function leerArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;

    setErrorParse('');
    setFilas(null);
    setResultado(null);
    setErrorImport('');
    setNombreArchivo(archivo.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const texto = ev.target?.result as string;
        const parsed = parsearCSV(texto);
        if (parsed.length === 0) {
          setErrorParse('No se encontraron filas válidas. Verificá el formato del archivo y que las columnas Documento y Nombre existan.');
          return;
        }
        setFilas(parsed);
      } catch (err) {
        setErrorParse(err instanceof Error ? err.message : 'Error al leer el archivo.');
      }
    };
    // Intentar UTF-8 primero (ideal si el CSV viene de Excel con BOM)
    reader.readAsText(archivo, 'UTF-8');
  }

  async function importar() {
    if (!filas || filas.length === 0) return;
    setImportando(true);
    setErrorImport('');
    try {
      const res = await api.post<ResultadoImport>('/api/portal/trabajadores/importar', {
        trabajadores: filas.map(({ dni, nombre, sexo, fechaNacimiento }) => ({
          dni,
          nombre,
          sexo,
          fechaNacimiento,
        })),
      });
      setResultado(res);
    } catch (err) {
      setErrorImport(err instanceof ErrorApi ? err.message : 'Error al importar los empleados.');
    } finally {
      setImportando(false);
    }
  }

  function reiniciar() {
    setFilas(null);
    setErrorParse('');
    setNombreArchivo('');
    setResultado(null);
    setErrorImport('');
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div
      className="capa-bloqueo"
      onClick={(e) => {
        if (e.target === e.currentTarget && !importando) onCerrar();
      }}
    >
      <div
        className="dialogo glass"
        style={{ maxWidth: 760, width: '95vw', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Cabecera */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '1px solid var(--borde)',
            paddingBottom: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>📥 Importar Empleados desde CSV / Excel</h3>
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.5 }}>
              Columnas del archivo: <strong>Documento</strong> · <strong>Nombre</strong> · <strong>Sexo</strong> · <strong>nacimiento</strong><br />
              Los empleados ya existentes (mismo DNI) se actualizan automáticamente.
            </p>
          </div>
          <button
            type="button"
            className="fantasma"
            onClick={onCerrar}
            disabled={importando}
            style={{ fontSize: 20, padding: 4, lineHeight: 1, flexShrink: 0 }}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        {/* ── Resultado exitoso ── */}
        {resultado ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
            <h3 style={{ margin: '0 0 8px' }}>¡Importación completada!</h3>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 28, flexWrap: 'wrap' }}>
              {[
                { val: resultado.insertados, label: 'nuevos', color: 'var(--primario)' },
                { val: resultado.actualizados, label: 'actualizados', color: '#f59e0b' },
                { val: resultado.total, label: 'total', color: 'var(--texto)' },
              ].map(({ val, label, color }) => (
                <div
                  key={label}
                  style={{
                    background: 'var(--fondo-tarjeta)',
                    border: '1px solid var(--borde)',
                    borderRadius: 12,
                    padding: '14px 24px',
                    minWidth: 90,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 30, fontWeight: 700, color }}>{val}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button type="button" className="secundario" style={{ width: 'auto' }} onClick={reiniciar}>
                Importar otro archivo
              </button>
              <button
                type="button"
                className="primario"
                style={{ width: 'auto' }}
                onClick={() => { onExito(); onCerrar(); }}
              >
                Listo
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* ── Zona de carga ── */}
            {!filas && (
              <>
                <div
                  style={{
                    border: '2px dashed var(--primario)',
                    borderRadius: 14,
                    padding: '32px 24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'color-mix(in srgb, var(--primario) 4%, transparent)',
                    transition: 'background 0.2s, border-color 0.2s',
                    marginBottom: 16,
                  }}
                  onClick={() => inputRef.current?.click()}
                >
                  <div style={{ fontSize: 44, marginBottom: 10 }}>📂</div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
                    Hacé clic para seleccionar un archivo CSV
                  </p>
                  <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                    Exportá la planilla de Excel como <strong>.csv</strong> (separador ; o ,) y seleccionala aquí
                  </p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    style={{ display: 'none' }}
                    onChange={leerArchivo}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <button
                    type="button"
                    className="fantasma"
                    onClick={descargarPlantilla}
                    style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    ⬇ Descargar plantilla de ejemplo (.csv)
                  </button>
                </div>

                {errorParse && (
                  <div className="error-box" style={{ whiteSpace: 'pre-line' }}>{errorParse}</div>
                )}

                {/* Instrucciones de cómo exportar desde Excel */}
                <div
                  style={{
                    background: 'var(--fondo-tarjeta)',
                    borderRadius: 10,
                    padding: 14,
                    fontSize: 12,
                    color: 'var(--texto-suave)',
                    border: '1px solid var(--borde)',
                  }}
                >
                  <strong style={{ color: 'var(--texto)', display: 'block', marginBottom: 6 }}>
                    💡 Cómo exportar desde Excel:
                  </strong>
                  Archivo → Guardar como → <em>CSV UTF-8 (delimitado por comas)</em> o <em>CSV (separado por punto y coma)</em>.<br />
                  Asegurate de que las columnas se llamen: <strong>Documento</strong>, <strong>Nombre</strong>, <strong>Sexo</strong>, <strong>nacimiento</strong>.
                </div>
              </>
            )}

            {/* ── Vista previa + confirmar ── */}
            {filas && filas.length > 0 && (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <strong style={{ fontSize: 14 }}>
                      Vista previa — {nombreArchivo}
                    </strong>
                    <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                      {filas.length} empleado{filas.length !== 1 ? 's' : ''} detectado{filas.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="fantasma"
                    onClick={reiniciar}
                    style={{ fontSize: 12, padding: '4px 8px' }}
                  >
                    ✕ Cambiar archivo
                  </button>
                </div>

                <div className="tabla-contenedor" style={{ marginBottom: 16, maxHeight: 280, overflowY: 'auto' }}>
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th>Documento</th>
                        <th>Nombre</th>
                        <th>Sexo</th>
                        <th>Fecha Nac.</th>
                        <th>Edad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.slice(0, 50).map((f, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace' }}>{f.dni}</td>
                          <td>{f.nombre}</td>
                          <td>
                            {f.sexo ? (
                              <span
                                className={`insignia ${f.sexo.toUpperCase().startsWith('H') || f.sexo.toUpperCase() === 'M' ? 'azul' : 'verde'}`}
                                style={{ fontSize: 10, padding: '2px 6px' }}
                              >
                                {f.sexo}
                              </span>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td>{f.fechaNacimiento ?? <span className="muted">—</span>}</td>
                          <td>{f._edadCalculada != null ? `${f._edadCalculada} años` : <span className="muted">—</span>}</td>
                        </tr>
                      ))}
                      {filas.length > 50 && (
                        <tr>
                          <td
                            colSpan={5}
                            style={{ textAlign: 'center', fontStyle: 'italic', color: 'var(--texto-suave)', padding: 8 }}
                          >
                            …y {filas.length - 50} más (se importarán todos)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {errorImport && (
                  <div className="error-box" style={{ marginBottom: 12 }}>{errorImport}</div>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="secundario"
                    onClick={onCerrar}
                    disabled={importando}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="primario"
                    onClick={importar}
                    disabled={importando}
                    style={{ width: 'auto', minWidth: 200 }}
                  >
                    {importando
                      ? `Importando ${filas.length} empleados…`
                      : `✅ Importar ${filas.length} empleado${filas.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
